import type { Ref } from 'vue';

/**
 * A collection of utility functions for making assertions during SSG builds.
 *
 * In production, these will log warnings in SSR mode and throw errors in SSG mode.
 *
 * If you are testing locally make sure to update your .env file to set
 * NUXT_BUILD_MODE to 'ssg' to see the errors thrown by these assertions.
 */
const isSsr = () => {
  return process.env.NUXT_BUILD_MODE === 'ssr';
};

const isClientSide = () => {
  return !import.meta.server;
};

// true if we should only log even if SSG mode.
let isWarnOnlyMode = false;

/**
 * Log in SSR or client side, throw in SSG. Useful for sanity checks during
 * build time without breaking runtime behavior.
 */
const throwOrLog = (message: string) => {
  if (isWarnOnlyMode || isClientSide() || isSsr()) {
    const suffix = isWarnOnlyMode ? ' (warn only mode)' : 'failed';
    if (import.meta.server) {
      console.warn(`AssertThat failed: ${message}${suffix}`);
    } else {
      console.warn(
        `%c AssertThat failed: ${message}${suffix}`,
        'background: #f5a800; color: black; font-weight: bold; font-size: 12px; border-radius: 3px;'
      );
    }
  } else {
    throw new Error(`AssertThat failed: ${message}`);
  }
};

/**
 * Assert that the provided condition is not null, undefined, an empty string,
 * NaN, an empty array, or an empty object.
 *
 * Only throws during SSG builds.
 */
export const isNotNothing = <T>(condition: T, message?: string): T => {
  if (typeof condition === 'boolean') {
    return condition;
  }

  if (condition === null || condition === undefined) {
    throwOrLog(message || 'Expected a non-null and non-undefined value.');
    return condition;
  }

  if (typeof condition === 'string' && condition.trim() === '') {
    throwOrLog(message || 'Expected a non-empty string.');
    return condition;
  }

  if (typeof condition === 'number') {
    if (isNaN(condition)) {
      throwOrLog(message || 'Expected a number that is not NaN.');
    }
    return condition;
  }

  if (typeof condition === 'object') {
    if (Array.isArray(condition) && condition.length === 0) {
      throwOrLog(message || 'Expected a non-empty array.');
    } else if (!Array.isArray(condition) && Object.entries(condition).length === 0) {
      throwOrLog(message || 'Expected a non-empty object.');
    }
    return condition;
  }

  return condition;
};

export const nonEmptyString = <T>(value: T, message?: string): T => {
  if (typeof value !== 'string' || value.trim() === '') {
    throwOrLog(message || 'Expected a non-empty string.');
  }

  return value;
};

export const nonEmptyStringOrDefault = <T extends string | null | undefined>(
  value: T,
  defaultValue: string,
  message?: string
): string => {
  if (typeof value !== 'string' || value.trim() === '') {
    throwOrLog(message || 'Expected a non-empty string.');
  }

  return value || defaultValue;
};

export const nonEmptyArray = <T>(value: Array<T>, message?: string): Array<T> => {
  if (!Array.isArray(value) || value.length === 0) {
    throwOrLog(message || 'Expected a non-empty array.');
  }
  return value;
};

export const nonEmptyObject = <T extends object>(value: T, message?: string): T => {
  if (typeof value !== 'object' || value === null || Object.entries(value).length === 0) {
    throwOrLog(message || 'Expected a non-empty object.');
  }
  return value;
};

/**
 * Assert that the provided ref is non-null. Only throws during SSG builds. The
 * runtime value CAN be null in SSR mode.
 */
export const nonNullRef = <T>(value: Ref<T | null>, message?: string): Ref<T | null> => {
  if (value.value === null) {
    throwOrLog(message || 'Expected a non-null ref value.');
  }

  return value;
};

/**
 * Get the first element of the provided array ref if it is non-null else throw.
 */
export const getFirstOrThrow = <T>(value: Ref<T[] | null, T[] | null>, message?: string): T => {
  if (!Array.isArray(value.value) || value.value.length === 0 || value.value[0] === null) {
    throw new Error(message || 'Expected a non-empty array ref with a non-null first element.');
  }

  // if it is not null, then we assume its a T
  return value.value[0] as T;
};

/** Get the value and throw if its null/undefined. */
export const getNonNullOrThrow = <T>(val: T | null | undefined, message?: string): T => {
  if (val === null || val === undefined) {
    throw new Error(message || 'Expected a non-null and non-undefined value.');
  }

  return val;
};

/** Get the value and throw if its null/undefined. */
export const getNonNullRefOrThrow = <T>(
  arg: Ref<T | null | undefined>,
  message?: string
): Ref<T> => {
  if (arg.value === null || arg.value === undefined) {
    throw new Error(message || 'Expected a non-null and non-undefined value.');
  }

  return arg as Ref<T>;
};

/**
 * Assert that at least one of the provided values is non-null/undefined. Only throws during SSG builds.
 */
export const someNonNull = (...values: Array<unknown>): void => {
  if (values.some((value) => value !== null && value !== undefined)) {
    return;
  }

  throwOrLog('Expected at least one non-null/undefined value, but all values were null/undefined.');
};

/**
 * Assert that all of the provided values are non-null/undefined. Only throws
 * during SSG builds.
 */
export const allNonNull = (...values: Array<unknown>): void => {
  for (const value of values) {
    if (value === null || value === undefined) {
      throwOrLog('Expected all values to be non-null/undefined, but found a null/undefined value.');
    }
  }
};

/**
 * Assert that the provided value is null or undefined. Only throws during SSG
 * builds.
 */
export const isNullOrUndefined = (value: unknown, message?: string): void => {
  if (value === null || value === undefined) {
    return;
  }
  throwOrLog(message || 'Expected value to be null or undefined.');
};

/**
 * Run the provided assertion function in "warn only" mode, which will log
 * warnings instead of throwing errors during SSG builds.
 *
 * Used before we are confident in our assertions.
 */
export const warnOnly = <T>(assertion: () => T): T => {
  try {
    isWarnOnlyMode = true;
    return assertion();
  } finally {
    isWarnOnlyMode = false;
  }
};
